/**
 * ================================================================================================
 *  הפרטים שלי — a person's own details, and a change of the login address that is proved.
 * ================================================================================================
 *
 * The Owner, 2026-10-29, section 1:
 *
 *     *"כל משתמש יוכל לערוך את הפרטים וההעדפות האישיים שלו, לרבות שם ופרטי קשר. שינוי מייל כניסה
 *     יכלול אימות הכתובת החדשה והודעה לכתובת הישנה ... משתמש לא יוכל לשנות לעצמו תפקידים,
 *     הרשאות, גישה לחברות או זכאות למודולים."*
 *
 * ── WHAT THIS SCREEN DELIBERATELY DOES NOT HAVE ────────────────────────────────────────────────
 *
 * No role, no permission, no company and no module — not disabled, ABSENT. A disabled control is a
 * statement that the thing is nearly available and that somebody is being held back from it; what
 * is true here is that this screen is not where those live. Each has its own screen under its own
 * capability, and the server refuses any of them in a body sent to `user.profile.update` BY NAME
 * rather than ignoring it, which is the Owner's own correction: *"טיפוס TypeScript אינו בקרת
 * אבטחה לבקשה חיצונית."*
 *
 * And NO PASSWORD FIELD. Changing a password is `PersonalSecurityScreen`'s, through the mechanism
 * that already exists: *"שינוי סיסמה וניהול אימות דו־שלבי ייעשו באמצעות המנגנונים המאובטחים
 * הקיימים."* A second place to type a password is a second place for one to leak.
 *
 * ── THE ADDRESS IS A SEPARATE FORM, AND THAT IS THE POINT ──────────────────────────────────────
 *
 * Changing a name is immediate. Changing the address is a REQUEST: a link goes to the new address,
 * a notice goes to the old one, and nothing moves until the link is followed. Putting both in one
 * form with one Save would make one of those two sentences a lie — a person would press Save and
 * reasonably believe their address had changed.
 */

import { useCallback, useState } from 'react';
import type { JSX } from 'react';

import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { SurfaceNotice } from '../components/SurfaceNotice';
import { ServerRead } from '../components/ServerRead';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { StateBlock } from '../components/StateBlock';
import { classifyActionError, type ActionFailure } from '../api/actionOutcome';
import { callOperation } from '../api/operations';
import { useServerData } from '../api/useServerData';
import { useLocale } from '../i18n/LocaleProvider';
import { routes } from '../routes';

interface PendingEmailChange {
  readonly newEmail: string;
  readonly requestedAtUtc: string;
  readonly expiresAtUtc: string;
}

interface ProfileView {
  readonly displayName: string;
  readonly email: string;
  readonly phone: string | null;
  readonly loginFollowsEmail: boolean;
  readonly pendingEmailChange: PendingEmailChange | null;
}

type Outcome =
  | { readonly kind: 'saved' }
  | { readonly kind: 'requested'; readonly address: string }
  | { readonly kind: 'failed'; readonly failure: ActionFailure };

export function PersonalDetailsScreen(): JSX.Element {
  const { t } = useLocale();
  const ctx = useWorkContext();

  const read = useCallback(() => callOperation('user.profile.read', {}), []);
  const profileRead = useServerData(read);

  /*
   * `null` ⇒ NOTHING TYPED YET, so the field shows what the server returned. The two are different
   * facts and `''` cannot tell them apart — which matters here more than usual, because an empty
   * string in the phone field is a real instruction: clear it.
   */
  const [name, setName] = useState<string | null>(null);
  const [phone, setPhone] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const saveDetails = async (event: React.FormEvent, current: ProfileView): Promise<void> => {
    event.preventDefault();
    setSending(true);
    setOutcome(null);
    try {
      await callOperation('user.profile.update', {
        /*
         * EXACTLY THE TWO FIELDS THE ROUTE SERVES. Not a spread of some state object: a spread is
         * how a field nobody meant to send gets sent, and the server would refuse the request by
         * name — correctly, and confusingly, because nothing on this screen would explain it.
         */
        body: {
          displayName: name ?? current.displayName,
          phone: phone ?? current.phone,
        },
      });
      profileRead.reload();
      setOutcome({ kind: 'saved' });
      setName(null);
      setPhone(null);
    } catch (error) {
      setOutcome({ kind: 'failed', failure: classifyActionError(error) });
    } finally {
      setSending(false);
    }
  };

  const requestAddress = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setSending(true);
    setOutcome(null);
    try {
      await callOperation('user.email.change_request', { body: { newEmail } });
      profileRead.reload();
      setOutcome({ kind: 'requested', address: newEmail });
      setNewEmail('');
    } catch (error) {
      setOutcome({ kind: 'failed', failure: classifyActionError(error) });
    } finally {
      setSending(false);
    }
  };

  if (ctx.surface !== 'TENANT' && ctx.surface !== 'PLATFORM') {
    return <SurfaceNotice titleHe={t('profile.title')} requires="TENANT" actual={ctx.surface} />;
  }

  return (
    <AppShell
      translated
      titleHe={t('profile.title')}
      screenId="personal-details"
      backToParent={{ labelHe: t('nav.security'), href: routes.personalSecurity }}
    >
      <ServerRead<ProfileView> result={profileRead.result} whatHe={t('profile.title')}>
        {(profile) => (
          <>
            {/* ── NAME AND CONTACT ──────────────────────────────────────────────────────── */}
            <section className="gp-panel">
              <div className="gp-section-heading">
                <h2>{t('profile.detailsHeading')}</h2>
              </div>

              <form className="form" onSubmit={(e) => void saveDetails(e, profile)}>
                <label className="field">
                  <span className="field__label">{t('profile.nameLabel')}</span>
                  <input
                    className="field__input"
                    name="displayName"
                    value={name ?? profile.displayName}
                    onChange={(e) => setName(e.currentTarget.value)}
                    maxLength={128}
                    required
                  />
                </label>

                <label className="field">
                  <span className="field__label">{t('profile.phoneLabel')}</span>
                  <input
                    className="field__input"
                    name="phone"
                    dir="ltr"
                    inputMode="tel"
                    value={phone ?? profile.phone ?? ''}
                    onChange={(e) => setPhone(e.currentTarget.value)}
                    maxLength={32}
                  />
                  {/* CLEARING IS A REAL ACT, and the hint says so rather than leaving a person to
                      discover that an empty field is ignored — because here it is not. */}
                  <span className="field__hint">{t('profile.phoneHint')}</span>
                </label>

                <div className="actions-row">
                  <button type="submit" className="btn btn--primary" disabled={sending}>
                    {sending ? t('profile.saving') : t('profile.save')}
                  </button>
                </div>
              </form>
            </section>

            {/* ── THE LOGIN ADDRESS ─────────────────────────────────────────────────────── */}
            <section className="gp-panel">
              <div className="gp-section-heading">
                <h2>
                  {profile.loginFollowsEmail ? t('profile.loginAddress') : t('profile.contactAddress')}
                </h2>
              </div>

              <p className="gp-small">
                <span className="gp-muted">{t('profile.currentAddress')}: </span>
                <span dir="ltr">{profile.email}</span>
              </p>

              {/*
                WHICH IT IS, SAID RATHER THAN ASSUMED. For a tenant account the login IS the
                address, and changing it changes how the person signs in; a platform staff account
                whose login is a username is changing a contact detail. The server decides which
                and the screen repeats it — it does not work it out a second way.
              */}
              <p className="gp-small gp-muted">
                {profile.loginFollowsEmail ? t('profile.loginFollows') : t('profile.loginSeparate')}
              </p>

              {profile.pendingEmailChange === null ? (
                <form className="form" onSubmit={(e) => void requestAddress(e)}>
                  <label className="field">
                    <span className="field__label">{t('profile.newAddressLabel')}</span>
                    <input
                      className="field__input"
                      name="newEmail"
                      type="email"
                      dir="ltr"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.currentTarget.value)}
                      maxLength={320}
                      required
                    />
                    <span className="field__hint">{t('profile.newAddressHint')}</span>
                  </label>

                  <div className="actions-row">
                    <button
                      type="submit"
                      className="btn btn--primary"
                      disabled={sending || newEmail.trim() === ''}
                    >
                      {sending ? t('profile.requesting') : t('profile.requestChange')}
                    </button>
                  </div>
                </form>
              ) : (
                /*
                  A REQUEST IS OUTSTANDING, so the form is replaced rather than disabled: what a
                  person needs here is not a greyed-out box but the state they are actually in, the
                  address it is waiting on, and when it stops waiting.
                */
                <StateBlock
                  titleHe={t('profile.pendingTitle')}
                  bodyHe={t('profile.pendingBody').replace(
                    '%s',
                    profile.pendingEmailChange.newEmail,
                  )}
                  nextStep={<p className="gp-small">{t('profile.pendingNext')}</p>}
                />
              )}
            </section>

            {/* ── WHAT IS NOT HERE, AND WHERE IT IS ─────────────────────────────────────── */}
            <section className="gp-panel">
              <div className="gp-section-heading">
                <h2>{t('profile.elsewhereHeading')}</h2>
              </div>
              <ul className="gp-small">
                <li>
                  <a href={routes.personalSecurity}>{t('profile.elsewhereSecurity')}</a>
                </li>
                {/*
                  ROLES AND ACCESS ARE NAMED AND NOT LINKED. A person without
                  `tenant.user.manage` following a link here would meet a refusal, which is a next
                  step that ends in a wall — the same defect as a forbidden read in a smaller
                  place. What they need is to know WHO to ask.
                */}
                <li className="gp-muted">{t('profile.elsewherePermissions')}</li>
              </ul>
            </section>

            {outcome === null ? null : outcome.kind === 'failed' ? (
              <ActionFailureNotice failure={outcome.failure} />
            ) : outcome.kind === 'saved' ? (
              <StateBlock titleHe={t('profile.savedTitle')} bodyHe={t('profile.savedBody')} />
            ) : (
              /*
                THE CONFIRMATION NAMES BOTH MESSAGES, because both were sent and the second one is
                the one a person did not ask for and must not be surprised by.
              */
              <StateBlock
                titleHe={t('profile.requestedTitle')}
                bodyHe={t('profile.requestedBody').replace('%s', outcome.address)}
                nextStep={<p className="gp-small">{t('profile.requestedNotice')}</p>}
              />
            )}
          </>
        )}
      </ServerRead>
    </AppShell>
  );
}
